import type {Sorting} from '@igp/shared/api';
import {useAuthUser} from '@igp/shared/auth';
import {type MapResult, useAutoMapper} from '@igp/shared/auto-mapper';
import type {AgentRefereeRevenueReport} from '@igp/shared/contract/pam';
import type {UseListViewEntityResult, UseViewEntityProps} from '@igp/shared/redux/view';
import {extendedViewCleanDelay} from '@igp/shared/redux/view';
import {useColumnsSettings} from '@igp/shared/user-settings';
import {EntityType} from '@models';
import {useViewInit} from '@redux';

import {agentLevelReportV2MappingKeys} from './mapper';
import type {
    AgentLevelReportV2ColumnSetting,
    AgentLevelReportV2ColumnSettingKeys,
    AgentLevelReportV2QueryKeys,
    AgentLevelReportV2ServerFilterKeys,
    AgentLevelReportV2SortKeys,
    AgentLevelReportV2ViewModel,
    AgentLevelReportV2ViewModelsKeys,
    DataGridAgentLevelReportV2Keys,
} from './types';

export type UseAgentLevelReportV2Props = Omit<
    UseViewEntityProps<EntityType, AgentLevelReportV2ServerFilterKeys, AgentLevelReportV2ViewModelsKeys>,
    'schemaMapper' | 'entity' | 'cleanDelay'
> & {
    fields: AgentLevelReportV2ViewModelsKeys[];
    saveFilters?: boolean;
};

type UseAgentLevelReportV2Result = UseListViewEntityResult<AgentLevelReportV2ViewModel, AgentLevelReportV2ViewModelsKeys>;

export function useAgentLevelReportV2({
    viewType,
    fields = [],
    saveFilters = true,
    defaultFilters,
    defaultSorting,
    defaultPaging,
    syncWithUrl,
    validateFilter,
    ...props
}: UseAgentLevelReportV2Props): UseAgentLevelReportV2Result {
    const mapper = useAutoMapper();
    const queryFields = fields.map(f =>
        mapper.map<AgentLevelReportV2ViewModelsKeys, AgentLevelReportV2QueryKeys>(
            f,
            agentLevelReportV2MappingKeys.ViewModelsKeys,
            agentLevelReportV2MappingKeys.QueryKeys
        )
    );
    const {
        items: serverItems,
        totalCount,
        getSearchFilter,
        viewEntity,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange: onSortChange,
    } = useViewInit<AgentRefereeRevenueReport, AgentLevelReportV2ServerFilterKeys, AgentLevelReportV2QueryKeys, AgentLevelReportV2SortKeys>(
        {
            viewType,
            entity: {
                entity: EntityType.AgentLevelReporV2,
                fields: queryFields,
            },
            cleanDelay: saveFilters ? extendedViewCleanDelay : 0,
            defaultFilters,
            defaultPaging,
            defaultSorting: mapSorting(defaultSorting),
            syncWithUrl,
            validateFilter,
            ...props,
        }
    );

    const items: MapResult<AgentLevelReportV2ViewModel>[] = serverItems.map(i =>
        mapper.tryMap<AgentRefereeRevenueReport, AgentLevelReportV2ViewModel>(
            i,
            agentLevelReportV2MappingKeys.Model,
            agentLevelReportV2MappingKeys.ViewModel
        )
    );

    function mapSortField(vmKey: AgentLevelReportV2ViewModelsKeys): AgentLevelReportV2SortKeys {
        return mapper.map<AgentLevelReportV2ViewModelsKeys, AgentLevelReportV2SortKeys>(
            vmKey,
            agentLevelReportV2MappingKeys.ViewModelsKeys,
            agentLevelReportV2MappingKeys.SortKeys
        );
    }

    function mapSorting(sorting: Sorting<AgentLevelReportV2ViewModelsKeys>[]): Sorting<AgentLevelReportV2SortKeys>[] {
        return sorting?.map(i => ({field: mapSortField(i.field), sort: i.sort})) ?? [];
    }

    function handleSortChange(newSorting: Sorting<AgentLevelReportV2ViewModelsKeys>[]): void {
        onSortChange(mapSorting(newSorting));
    }

    return {
        items: items?.map(i => i.model),
        rawItems: items?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        totalCount,
        searchFilter: getSearchFilter(i =>
            mapper.map<AgentLevelReportV2SortKeys, AgentLevelReportV2ViewModelsKeys>(
                i,
                agentLevelReportV2MappingKeys.SortKeys,
                agentLevelReportV2MappingKeys.ViewModelsKeys
            )
        ),
        filterString: viewEntity?.filter,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
    };
}

type UseAgentLevelReportV2SettingsPorps = {
    settingId: string;
    defaultColumns: DataGridAgentLevelReportV2Keys[];
    pinnedColumns?: DataGridAgentLevelReportV2Keys[];
};

type UseAgentLevelReportV2SettingsResult = {
    visibleColumns?: DataGridAgentLevelReportV2Keys[];
    onVisibleColumnsChange: (value: DataGridAgentLevelReportV2Keys[]) => void;
};

export function useAgentLevelReportV2Settings({
    settingId,
    defaultColumns,
    pinnedColumns = [],
}: UseAgentLevelReportV2SettingsPorps): UseAgentLevelReportV2SettingsResult {
    const {sub} = useAuthUser();
    const mapper = useAutoMapper();
    const {visible, onVisibleColumnsChange} = useColumnsSettings<AgentLevelReportV2ColumnSetting>({
        userId: sub,
        settingId,
        defaultColumns: mapViewModelKeysToSettings(defaultColumns),
        pinnedColumns: mapViewModelKeysToSettings(pinnedColumns),
    });

    function mapViewModelKeysToSettings(keys: DataGridAgentLevelReportV2Keys[]): AgentLevelReportV2ColumnSettingKeys[] {
        return [
            ...new Set(
                keys?.map(i =>
                    mapper.map<DataGridAgentLevelReportV2Keys, AgentLevelReportV2ColumnSettingKeys>(
                        i,
                        agentLevelReportV2MappingKeys.ColumnKeys,
                        agentLevelReportV2MappingKeys.SettingKeys
                    )
                ) ?? []
            ),
        ];
    }

    function mapSettingsToViewModelKeys(settings: AgentLevelReportV2ColumnSettingKeys[]): DataGridAgentLevelReportV2Keys[] {
        return [
            ...new Set(
                settings?.map(i =>
                    mapper.map<AgentLevelReportV2ColumnSettingKeys, DataGridAgentLevelReportV2Keys>(
                        i,
                        agentLevelReportV2MappingKeys.SettingKeys,
                        agentLevelReportV2MappingKeys.ColumnKeys
                    )
                ) ?? []
            ),
        ];
    }

    function handleColumnSettingsChange(fields: DataGridAgentLevelReportV2Keys[]) {
        const columns = mapViewModelKeysToSettings(fields);
        onVisibleColumnsChange(columns);
    }

    return {
        visibleColumns: mapSettingsToViewModelKeys(visible).filter(f => defaultColumns.includes(f)),
        onVisibleColumnsChange: handleColumnSettingsChange,
    };
}
